/* ============================================================
   Nutriride — theme-built faceted filters
   ------------------------------------------------------------
   Why this exists instead of Shopify Search & Discovery:

   S&D builds facets from raw catalog values, and this catalog is
   not clean. Flavour is split across four option names
   (Flavours 119 / Flavor 103 / Flavors 44 / Flavour 20), Size has
   161 values of which 101 are used by one product, and
   product_type has 121 values with 48% of products blank. Native
   filtering would faithfully reproduce that mess. This layer
   normalises on read, so the shopper sees one Flavour group and
   one "1 kg", today, with no catalog migration.

   How it works:
     1. facet data from /collections/<handle>/products.json
     2. cards from Warehouse's OWN section render, indexed by
        handle — so a filtered card is byte-identical to an
        unfiltered one (labels, ratings, cheapest-in-stock price,
        quick view all intact)
     3. filtering, counting, sorting and paging client-side

   Boundary: with no filter selected this does NOTHING, and
   Warehouse's server-side pagination and sort behave exactly as
   they do today. It only takes over the grid once a filter is on.
   ============================================================ */
(function () {
  'use strict';

  var PAGE_SIZE = 24;     // matches Warehouse's default items_per_page
  var CONCURRENCY = 4;    // parallel section fetches
  var MIN_VALUES = 2;     // a facet with one value filters nothing
  var MIN_TAG_USES = 3;   // below this a tag is noise, not a facet
  var PARAM = 'nf_';      // URL param prefix, e.g. ?nf_brand=Labrada,Scitron

  /* ---------------------------------------------------------
     Normalisation — the whole reason this layer exists
     --------------------------------------------------------- */

  function clean(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  }

  function titleCase(s) {
    return clean(s).toLowerCase().replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  function num(n) {
    // 1.0 -> "1", 2.50 -> "2.5"
    return String(parseFloat(n.toFixed(2)));
  }

  /** Four option spellings collapse to one group; flavour text is title-cased. */
  function isFlavourOption(name) { return /^flavou?rs?$/i.test(clean(name)); }
  function isSizeOption(name) { return /^sizes?$/i.test(clean(name)); }

  function normFlavour(v) {
    var s = clean(v).replace(/[.,]+$/, '');
    if (!s) return null;
    if (/^(un ?flavou?red|no flavou?r|plain|unflavou?red)$/i.test(s)) return 'Unflavoured';
    return titleCase(s);
  }

  /**
   * "1Kg", "1 kg", "1KG", "1000g" all mean the same shelf unit.
   * Canonicalise the number and the unit; fall back to title case
   * rather than inventing a bucket we cannot justify.
   */
  var UNITS = [
    [/^(kgs?|kilograms?)$/i, 'kg'],
    [/^(gm?s?|grams?)$/i, 'g'],
    [/^(lbs?|pounds?)$/i, 'lb'],
    [/^(ml|millilitres?|milliliters?)$/i, 'ml'],
    [/^(l|litres?|liters?)$/i, 'L'],
    [/^(caps?|capsules?)$/i, 'Capsules'],
    [/^(tabs?|tablets?)$/i, 'Tablets'],
    [/^(softgels?)$/i, 'Softgels'],
    [/^(servings?|serves?)$/i, 'Servings'],
    [/^(sachets?)$/i, 'Sachets'],
    [/^(pcs?|pieces?|count|ct)$/i, 'Pieces']
  ];

  function normSize(v) {
    var s = clean(v);
    if (!s) return null;
    var m = s.match(/^([\d]+(?:\.\d+)?)\s*([a-z]+)\.?$/i);
    if (!m) return titleCase(s);
    var value = parseFloat(m[1]);
    var unit = null;
    for (var i = 0; i < UNITS.length; i++) {
      if (UNITS[i][0].test(m[2])) { unit = UNITS[i][1]; break; }
    }
    if (!unit) return titleCase(s);
    // 1000 g and 1 kg are the same thing on a shelf.
    if (unit === 'g' && value >= 1000 && value % 1000 === 0) { value = value / 1000; unit = 'kg'; }
    return num(value) + ' ' + unit;
  }

  /** Form is not a field on any product; it is readable from the size unit and the title. */
  function deriveForm(p, sizes) {
    var hay = (p.title + ' ' + (p.product_type || '') + ' ' + sizes.join(' ')).toLowerCase();
    if (/\bsoftgel/.test(hay)) return 'Softgels';
    if (/\bcapsule|\bcaps\b/.test(hay)) return 'Capsules';
    if (/\btablet|\btabs\b/.test(hay)) return 'Tablets';
    if (/\bsachet/.test(hay)) return 'Sachets';
    if (/\bml\b|\bliquid\b|\bshot\b|\bjuice\b|\bsyrup\b/.test(hay)) return 'Liquid';
    if (/\bkg\b|\bg\b|\bpowder\b|\bgrams?\b/.test(hay)) return 'Powder';
    return null;
  }

  /**
   * product_type is blank on 48% of products and spread over 121
   * values. Map to a short taxonomy by keyword, falling back to the
   * title when the field is empty. Order matters — "isolate" must be
   * tested before "whey".
   */
  var TYPE_RULES = [
    [/\bisolate\b/, 'Whey Isolate'],
    [/\bhydrolys|hydroliz/, 'Whey Hydrolysate'],
    [/mass gainer|weight gainer|\bgainer\b/, 'Mass Gainer'],
    [/pre[\s-]?workout/, 'Pre-Workout'],
    [/post[\s-]?workout/, 'Post-Workout'],
    [/\bcreatine\b/, 'Creatine'],
    [/\bbcaa\b/, 'BCAA'],
    [/\beaa\b/, 'EAA'],
    [/multi ?vitamin/, 'Multivitamin'],
    [/fish oil|\bomega\b/, 'Omega 3'],
    [/fat burner|carnitine|\blipo\b/, 'Fat Burner'],
    [/\bglutamine\b/, 'Glutamine'],
    [/arginine|citrulline|nitric oxide/, 'Nitric Oxide'],
    [/\bshilajit\b/, 'Shilajit'],
    [/ashwagandha/, 'Ashwagandha'],
    [/\bcollagen\b/, 'Collagen'],
    [/\bplant\b|\bvegan\b|\bpea protein\b|soy protein/, 'Plant Protein'],
    [/\bcasein\b/, 'Casein'],
    [/\bwhey\b/, 'Whey Protein'],
    [/\bprotein\b/, 'Protein']
  ];

  function normType(p) {
    var hay = clean(p.product_type).toLowerCase();
    if (!hay) hay = clean(p.title).toLowerCase(); // fills the 48% blank
    for (var i = 0; i < TYPE_RULES.length; i++) {
      if (TYPE_RULES[i][0].test(hay)) return TYPE_RULES[i][1];
    }
    return null;
  }

  var TAG_SKIP = /^(video|trial|hidden|new|sale|featured)$/i;

  var PRICE_BUCKETS = [
    { label: 'Under ₹1,000', min: 0, max: 100000 },
    { label: '₹1,000 – ₹2,000', min: 100000, max: 200000 },
    { label: '₹2,000 – ₹3,000', min: 200000, max: 300000 },
    { label: '₹3,000 – ₹5,000', min: 300000, max: 500000 },
    { label: 'Above ₹5,000', min: 500000, max: Infinity }
  ];

  /* ---------------------------------------------------------
     Facet definitions
     --------------------------------------------------------- */

  var FACETS = [
    { key: 'availability', label: 'Availability', values: function (p) { return p.inStock ? ['In stock'] : ['Out of stock']; } },
    { key: 'category', label: 'Category', values: function (p) { return p.type ? [p.type] : []; } },
    { key: 'brand', label: 'Brand', values: function (p) { return p.vendor ? [p.vendor] : []; } },
    { key: 'price', label: 'Price', values: function (p) { return p.priceBucket ? [p.priceBucket] : []; } },
    { key: 'form', label: 'Form', values: function (p) { return p.form ? [p.form] : []; } },
    { key: 'flavour', label: 'Flavour', values: function (p) { return p.flavours; } },
    { key: 'size', label: 'Size', values: function (p) { return p.sizes; } },
    { key: 'goal', label: 'Goal', values: function (p) { return p.goals; } }
  ];

  /* ---------------------------------------------------------
     State
     --------------------------------------------------------- */

  var state = {
    handle: null,
    sectionId: null,
    products: [],
    order: {},          // handle -> position in the server's render order
    selected: {},       // key -> [values]
    query: '',          // free-text filter from the header category picker
    cards: {},          // handle -> card element
    cardsReady: false,
    cardsLoading: null,
    valid: {},
    pageHandles: [],
    applying: false,
    rendered: 0,
    matches: [],
    originalGrid: null,
    originalFoot: null,
    built: false
  };

  /* ---------------------------------------------------------
     Data
     --------------------------------------------------------- */

  function collectionHandle() {
    var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function fetchProducts() {
    // The section starts this during parse and fetches every page in parallel.
    // Falling through to the sequential version below only matters if that
    // inline script did not run.
    if (window.__nrFacetProducts && typeof window.__nrFacetProducts.then === 'function') {
      return window.__nrFacetProducts;
    }

    var all = [];
    function page(n) {
      return fetch('/collections/' + state.handle + '/products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) {
          var batch = (d && d.products) || [];
          all = all.concat(batch);
          // 250 is the cap; a full page means there may be another.
          if (batch.length === 250 && n < 8) return page(n + 1);
          return all;
        });
    }
    return page(1);
  }

  function buildProduct(p) {
    var flavours = [], sizes = [], rawSizes = [];
    (p.options || []).forEach(function (o) {
      if (isFlavourOption(o.name)) {
        (o.values || []).forEach(function (v) { var n = normFlavour(v); if (n && flavours.indexOf(n) < 0) flavours.push(n); });
      } else if (isSizeOption(o.name)) {
        (o.values || []).forEach(function (v) {
          rawSizes.push(v);
          var n = normSize(v); if (n && sizes.indexOf(n) < 0) sizes.push(n);
        });
      }
    });

    var variants = p.variants || [];
    var inStock = variants.some(function (v) { return v.available; });
    // Price the card the way the grid prices it: cheapest in-stock variant.
    var pool = variants.filter(function (v) { return v.available; });
    if (!pool.length) pool = variants;
    var min = pool.reduce(function (a, v) {
      var c = Math.round(parseFloat(v.price) * 100);
      return a === null || c < a ? c : a;
    }, null);

    var bucket = null;
    if (min !== null) {
      for (var i = 0; i < PRICE_BUCKETS.length; i++) {
        if (min >= PRICE_BUCKETS[i].min && min < PRICE_BUCKETS[i].max) { bucket = PRICE_BUCKETS[i].label; break; }
      }
    }

    var goals = (p.tags || [])
      .map(clean)
      .filter(function (t) { return t && !TAG_SKIP.test(t); })
      .map(titleCase);

    return {
      handle: p.handle,
      title: clean(p.title),
      vendor: clean(p.vendor) || null,
      type: normType(p),
      flavours: flavours,
      sizes: sizes,
      form: deriveForm(p, rawSizes),
      goals: goals,
      inStock: inStock,
      price: min === null ? 0 : min,
      priceBucket: bucket,
      created: p.created_at || ''
    };
  }

  /* ---------------------------------------------------------
     Matching
     --------------------------------------------------------- */

  function facetByKey(key) {
    for (var i = 0; i < FACETS.length; i++) if (FACETS[i].key === key) return FACETS[i];
    return null;
  }

  /** A product matches a facet if it carries ANY selected value (OR within a group, AND across groups). */
  function matchesFacet(p, key, chosen) {
    if (!chosen || !chosen.length) return true;
    var vals = facetByKey(key).values(p);
    for (var i = 0; i < vals.length; i++) if (chosen.indexOf(vals[i]) >= 0) return true;
    return false;
  }

  function matching(except) {
    var q = (state.query || '').toLowerCase();
    return state.products.filter(function (p) {
      // ?nf_q arrives from the header category picker: "search within this
      // category". Matched on the title, which every product has, rather than
      // on product_type, which half the catalogue is missing.
      if (q && p.title.toLowerCase().indexOf(q) === -1) return false;
      for (var key in state.selected) {
        if (key === except) continue;
        if (!matchesFacet(p, key, state.selected[key])) return false;
      }
      return true;
    });
  }

  function activeCount() {
    var n = state.query ? 1 : 0;
    for (var k in state.selected) n += state.selected[k].length;
    return n;
  }

  /* ---------------------------------------------------------
     Cards — harvested from Warehouse's own render
     --------------------------------------------------------- */

  /**
   * Record handles per page index, never in arrival order — the fetches run in
   * parallel and resolve out of order. Flattened by page, this reproduces
   * exactly the order the server rendered, including the active sort_by.
   */
  function harvest(root, pageIndex) {
    var handles = [];
    Array.prototype.forEach.call(root.querySelectorAll('.product-item'), function (item) {
      var link = item.querySelector('a[href*="/products/"]');
      if (!link) return;
      var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      if (!state.cards[m[1]]) state.cards[m[1]] = item;
      if (handles.indexOf(m[1]) < 0) handles.push(m[1]);
    });
    state.pageHandles[pageIndex] = handles;
  }

  /**
   * Carries the live query string — crucially sort_by — into each section
   * fetch. Without it, page 1 (read from the document) is sorted and pages 2+
   * come back in the default order, interleaving into nonsense. Our own nf_
   * params are stripped; the server does not know them.
   */
  function sectionUrl(page) {
    var url = new URL(window.location.href);
    Array.from(url.searchParams.keys()).forEach(function (k) {
      if (k.indexOf(PARAM) === 0) url.searchParams.delete(k);
    });
    url.searchParams.set('section_id', state.sectionId);
    url.searchParams.set('page', page);
    return url.pathname + url.search;
  }

  function loadCards() {
    if (state.cardsReady) return Promise.resolve();
    if (state.cardsLoading) return state.cardsLoading;

    state.pageHandles = [];

    // Page 1 is fetched like every other page rather than read from the
    // document. Warehouse can dispatch theme:loading:end before it swaps the
    // new grid in, so the document may still hold OUR last filtered render —
    // harvesting that produced a page 1 in the wrong order. One extra request
    // buys a guaranteed-consistent ordering.
    var pages = Math.ceil(state.products.length / PAGE_SIZE);
    var queue = [];
    for (var p = 1; p <= pages; p++) queue.push(p);

    function worker() {
      if (!queue.length) return Promise.resolve();
      var p = queue.shift();
      return fetch(sectionUrl(p), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            harvest(doc, p - 1);
          }
        })
        .catch(function () { /* a missing page just means fewer cards, not a broken grid */ })
        .then(worker);
    }

    var workers = [];
    for (var i = 0; i < CONCURRENCY; i++) workers.push(worker());

    state.cardsLoading = Promise.all(workers).then(function () {
      var order = {}, n = 0;
      state.pageHandles.forEach(function (page) {
        (page || []).forEach(function (h) { if (!(h in order)) order[h] = n++; });
      });
      state.order = order;
      state.cardsReady = true;
      state.cardsLoading = null;
    });
    return state.cardsLoading;
  }

  /* ---------------------------------------------------------
     UI
     --------------------------------------------------------- */

  var CHEVRON = '<svg viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  var CHECK = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1 6l3.5 3.5L11 2.5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';

  function buildGroup(facet, values, scope) {
    var group = document.createElement('div');
    group.className = 'collection__filter-group';
    group.setAttribute('data-nr-facet', facet.key);

    var id = 'nrf-' + scope + '-' + facet.key;
    group.innerHTML =
      '<button type="button" class="collection__filter-group-name link link--secondary text--strong"' +
      ' data-action="toggle-collapsible" aria-controls="' + id + '" data-close-siblings="false"' +
      ' data-collapsible-force-overflow="true" aria-expanded="true">' + facet.label + CHEVRON + '</button>' +
      '<div id="' + id + '" class="collection__filter-collapsible" aria-hidden="false" style="height: auto; overflow: visible">' +
      '<ul class="collection__filter-checkbox-list" role="list"></ul></div>';

    var ul = group.querySelector('ul');
    values.forEach(function (v) {
      var vid = id + '-' + v.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      var li = document.createElement('li');
      li.className = 'collection__filter-checkbox';
      li.setAttribute('data-nr-value', v);
      li.setAttribute('data-nr-search', v.toLowerCase());
      li.innerHTML =
        '<div class="checkbox-wrapper">' +
        '<input type="checkbox" class="checkbox" id="' + vid + '" data-nr-facet="' + facet.key + '" value="' + v.replace(/"/g, '&quot;') + '">' +
        CHECK + '</div>' +
        '<label for="' + vid + '"><span class="nr-fv-label">' + v + '</span><span class="nr-fv-count">0</span></label>';
      ul.appendChild(li);
    });

    return group;
  }

  /** Values for a facet, ordered by how many products carry them. */
  function facetValues(facet) {
    var counts = {};
    state.products.forEach(function (p) {
      facet.values(p).forEach(function (v) { counts[v] = (counts[v] || 0) + 1; });
    });
    var keys = Object.keys(counts);
    if (facet.key === 'goal') keys = keys.filter(function (k) { return counts[k] >= MIN_TAG_USES; });
    if (facet.key === 'price') {
      keys.sort(function (a, b) {
        return PRICE_BUCKETS.findIndex(function (x) { return x.label === a; }) -
               PRICE_BUCKETS.findIndex(function (x) { return x.label === b; });
      });
    } else if (facet.key === 'availability') {
      keys.sort(); // "In stock" before "Out of stock"
    } else {
      keys.sort(function (a, b) { return counts[b] - counts[a] || a.localeCompare(b); });
    }
    return keys;
  }

  /**
   * Unlike the desktop sidebar, the mobile drawer emits its
   * .collection__filter-group elements straight into the form with no
   * wrapper. Give it one, in place of the native groups.
   */
  function mobileContainer() {
    var form = document.querySelector('#mobile-collection-filters-form');
    if (!form) return null;

    var made = form.querySelector('.nr-facet-mobile-list');
    if (made) return made;

    var native = Array.prototype.slice.call(form.querySelectorAll('.collection__filter-group'));
    if (!native.length) return null;

    var wrap = document.createElement('div');
    wrap.className = 'collection__filter-group-list nr-facet-mobile-list';
    native[0].parentNode.insertBefore(wrap, native[0]);
    native.forEach(function (el) { el.remove(); });
    return wrap;
  }

  function buildUI() {
    if (state.built) return;
    var containers = [
      { el: document.querySelector('#desktop-filters-form .collection__filter-group-list'), scope: 'd' },
      { el: mobileContainer(), scope: 'm' }
    ].filter(function (c) { return c.el; });
    if (!containers.length) return;

    var groups = FACETS.map(function (f) { return { facet: f, values: facetValues(f) }; })
      .filter(function (g) { return g.values.length >= MIN_VALUES; });
    if (!groups.length) return;

    // A shared link may carry a value this collection does not have (Form is a
    // facet on one collection and absent on the next). Remember what is real
    // here so readUrl can discard the rest instead of silently zeroing the grid.
    state.valid = {};
    groups.forEach(function (g) {
      state.valid[g.facet.key] = g.values;
    });

    containers.forEach(function (c) {
      // Shopify's two automatic filters are replaced wholesale: mixing a
      // server-side facet with client-side ones would reload the page and
      // drop the client-side selection.
      c.el.innerHTML = '';
      groups.forEach(function (g) { c.el.appendChild(buildGroup(g.facet, g.values, c.scope)); });
    });

    document.addEventListener('change', onToggle, true);
    state.built = true;
    revealFacets();
  }

  /* The sidebar is marked `nr-facets-pending` by the section before it paints,
     so Warehouse's two native facets are never shown and then swapped. Clearing
     the flag is what reveals ours. It must happen on every exit path — including
     a failed fetch — or the visitor is left with an empty Filters card. */
  function revealFacets() {
    document.documentElement.classList.remove('nr-facets-pending');
  }

  function onToggle(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input[data-nr-facet]')) return;
    event.stopPropagation(); // never reaches Warehouse's own filter handler

    var key = input.getAttribute('data-nr-facet');
    var value = input.value;
    var list = state.selected[key] || [];
    var at = list.indexOf(value);

    if (input.checked && at < 0) list.push(value);
    else if (!input.checked && at >= 0) list.splice(at, 1);

    if (list.length) state.selected[key] = list; else delete state.selected[key];

    syncInputs();
    writeUrl();
    apply();
  }

  /** Both copies of the sidebar (desktop + mobile drawer) share one selection. */
  function syncInputs() {
    Array.prototype.forEach.call(document.querySelectorAll('input[data-nr-facet]'), function (i) {
      var sel = state.selected[i.getAttribute('data-nr-facet')] || [];
      i.checked = sel.indexOf(i.value) >= 0;
    });
  }

  function updateCounts() {
    FACETS.forEach(function (facet) {
      // Faceted counts: how many results this value WOULD give, with every
      // other group's selection still applied.
      var pool = matching(facet.key);
      var counts = {};
      pool.forEach(function (p) {
        facet.values(p).forEach(function (v) { counts[v] = (counts[v] || 0) + 1; });
      });
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-nr-facet="' + facet.key + '"] .collection__filter-checkbox'),
        function (li) {
          var n = counts[li.getAttribute('data-nr-value')] || 0;
          var el = li.querySelector('.nr-fv-count');
          if (el) el.textContent = n;
          li.classList.toggle('is-empty', n === 0);
        }
      );
    });
    // A dedicated event, not theme:loading:end — that one now means "Warehouse
    // replaced the grid", and reusing it here would loop.
    document.dispatchEvent(new CustomEvent('nr:filters:refresh'));
  }

  /* ---------------------------------------------------------
     URL
     --------------------------------------------------------- */

  function writeUrl() {
    var url = new URL(window.location.href);
    // searchParams.keys() is an iterator with no length, so slice.call on it
    // silently yields [] and nothing is ever cleared. Array.from materialises it.
    Array.from(url.searchParams.keys()).forEach(function (k) {
      if (k.indexOf(PARAM) === 0) url.searchParams.delete(k);
    });
    url.searchParams.delete('nf_q');
    if (state.query) url.searchParams.set('nf_q', state.query);
    for (var key in state.selected) url.searchParams.set(PARAM + key, state.selected[key].join('|'));
    window.history.replaceState({}, '', url.toString());
  }

  function readUrl() {
    var params = new URL(window.location.href).searchParams;
    state.query = (params.get('nf_q') || '').trim();
    params.forEach(function (v, k) {
      if (k.indexOf(PARAM) !== 0) return;
      var key = k.slice(PARAM.length);
      if (!facetByKey(key) || !v) return;
      var offered = (state.valid && state.valid[key]) || [];
      var kept = v.split('|').filter(function (val) { return offered.indexOf(val) >= 0; });
      if (kept.length) state.selected[key] = kept;
    });
  }

  /* ---------------------------------------------------------
     Grid
     --------------------------------------------------------- */

  function grid() { return document.querySelector('.product-list--collection'); }

  /**
   * Sorting stays server-side: Warehouse reloads the section with sort_by and we
   * re-harvest, so "price, low to high" sorts the WHOLE collection, not just the
   * page. Here we only need to respect the order the server gave us.
   */
  function inServerOrder(list) {
    var order = state.order || {};
    return list.slice().sort(function (a, b) {
      var ai = a.handle in order ? order[a.handle] : 1e9;
      var bi = b.handle in order ? order[b.handle] : 1e9;
      return ai - bi;
    });
  }

  function renderChunk() {
    var g = grid();
    if (!g) return;
    var slice = state.matches.slice(state.rendered, state.rendered + PAGE_SIZE);
    slice.forEach(function (p) {
      var card = state.cards[p.handle];
      if (card) g.appendChild(card.cloneNode(true));
    });
    state.rendered += slice.length;
    updateFooter();
  }

  function updateFooter() {
    var foot = document.querySelector('.nr-facet-foot');
    if (!foot) return;
    var total = state.matches.length;
    var more = total - state.rendered;
    foot.querySelector('.nr-facet-foot__count').textContent =
      total ? 'Showing ' + state.rendered + ' of ' + total + ' product' + (total === 1 ? '' : 's')
            : 'No products match';
    var btn = foot.querySelector('.nr-facet-more');
    btn.hidden = more <= 0;
    btn.textContent = 'Load ' + Math.min(more, PAGE_SIZE) + ' more';
  }

  function ensureFooter() {
    var g = grid();
    if (!g || document.querySelector('.nr-facet-foot')) return;
    var foot = document.createElement('div');
    foot.className = 'nr-facet-foot';
    foot.innerHTML =
      '<p class="nr-facet-foot__count"></p>' +
      '<button type="button" class="nr-facet-more button button--secondary" hidden></button>';
    g.parentNode.insertBefore(foot, g.nextSibling);
    foot.querySelector('.nr-facet-more').addEventListener('click', renderChunk);
  }

  function setBusy(on) {
    var g = grid();
    if (g) g.classList.toggle('nr-facet-busy', !!on);
  }

  function restore() {
    var g = grid();
    if (g && state.originalGrid !== null) g.innerHTML = state.originalGrid;
    var foot = document.querySelector('.nr-facet-foot');
    if (foot) foot.remove();
    document.querySelectorAll('.nr-facet-hidden').forEach(function (el) { el.classList.remove('nr-facet-hidden'); });
    renderChips();
  }

  function apply() {
    updateCounts();
    renderChips();
    updateDrawerCount();

    if (!activeCount()) {
      restore();
      return;
    }

    var g = grid();
    if (!g) return;

    if (state.originalGrid === null) state.originalGrid = g.innerHTML;

    // Warehouse's own pagination and result count describe the unfiltered
    // collection, so they are hidden while a filter is on.
    document.querySelectorAll('.collection__dynamic-part .pagination').forEach(function (el) {
      el.classList.add('nr-facet-hidden');
    });

    setBusy(true);
    state.applying = true;
    loadCards().then(function () {
      state.matches = inServerOrder(matching(null));
      g.innerHTML = '';
      state.rendered = 0;
      ensureFooter();
      renderChunk();
      if (!state.matches.length) {
        g.innerHTML = '<p class="nr-facet-empty">No products match these filters. Try removing one.</p>';
      }
      setBusy(false);
      // Released on a later tick so the reload watcher ignores our own work.
      setTimeout(function () { state.applying = false; }, 150);
    });
  }

  /* ---------------------------------------------------------
     Active filter chips
     --------------------------------------------------------- */

  function renderChips() {
    Array.prototype.forEach.call(document.querySelectorAll('.collection__active-filters'), function (box) {
      box.innerHTML = '';
      var any = false;

      if (state.query) {
        any = true;
        var qchip = document.createElement('button');
        qchip.type = 'button';
        qchip.className = 'collection__active-filter-item';
        qchip.innerHTML = '<span class="collection__active-filter-cross">' +
          '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" fill="none"/></svg></span>';
        qchip.appendChild(document.createTextNode('\u201c' + state.query + '\u201d'));
        qchip.addEventListener('click', function () {
          state.query = '';
          writeUrl();
          apply();
        });
        box.appendChild(qchip);
      }

      for (var key in state.selected) {
        state.selected[key].forEach(function (v) {
          any = true;
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'collection__active-filter-item';
          chip.innerHTML = '<span class="collection__active-filter-cross">' +
            '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" fill="none"/></svg></span>';
          chip.appendChild(document.createTextNode(v));
          chip.addEventListener('click', function () { removeValue(key, v); });
          box.appendChild(chip);
        });
      }
      if (any) {
        var clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'collection__clear-filter button button--small button--transparent button--full';
        clear.textContent = 'Clear all';
        clear.addEventListener('click', clearAll);
        box.appendChild(clear);
      }
    });
  }

  /** The drawer header carries its own active-filter count. */
  function updateDrawerCount() {
    var n = activeCount();
    Array.prototype.forEach.call(document.querySelectorAll('.collection-drawer__filter-count'), function (el) {
      el.textContent = '(' + n + ')';
      el.style.display = n ? '' : 'none';
    });
  }

  function removeValue(key, value) {
    var list = state.selected[key] || [];
    var at = list.indexOf(value);
    if (at >= 0) list.splice(at, 1);
    if (!list.length) delete state.selected[key];
    syncInputs(); writeUrl(); apply();
  }

  function clearAll() {
    state.selected = {};
    state.query = '';
    syncInputs(); writeUrl(); apply();
  }

  /* ---------------------------------------------------------
     Sort — taken over only while filtering is active
     --------------------------------------------------------- */

  /**
   * Sort, page size and layout changes all go through Warehouse's own Section
   * Rendering reload, which replaces the grid and wipes our render. Rather than
   * fight it (the sort control is a ValuePicker, so intercepting its clicks
   * would also break its open/close), we let it happen and rebuild on top: the
   * reloaded markup is the new source of truth for order.
   */
  function watchReload() {
    var timer = null;
    document.addEventListener('theme:loading:end', function () {
      if (!activeCount()) return;       // unfiltered: nothing of ours to restore
      if (state.applying) return;       // our own render, not Warehouse's
      clearTimeout(timer);
      timer = setTimeout(function () {
        state.cards = {};
        state.cardsReady = false;
        state.cardsLoading = null;
        state.originalGrid = null;      // the server grid changed underneath us
        // _reload also does `desktopFilters.innerHTML = ...` and the same for
        // the drawer, so both sidebars are back to the native Availability/Price
        // groups and our facets have to be rebuilt from scratch.
        state.built = false;
        buildUI();
        syncInputs();
        // Warehouse pushState's its own `currentUrl`, captured at page load and
        // unaware of our params, so the filter state vanishes from the address
        // bar on every sort. Put it back.
        writeUrl();
        apply();
      }, 60);
    });
  }

  /* ---------------------------------------------------------
     Boot
     --------------------------------------------------------- */

  function init() {
    // Every one of these exits leaves the native sidebar in charge, so each
    // has to reveal it first — otherwise the pending flag hides the filters
    // permanently on any page the engine declines to run on.
    if (!document.body.classList.contains('template-collection')) { revealFacets(); return; }
    state.handle = collectionHandle();
    if (!state.handle) { revealFacets(); return; }

    state.count = window.__nrCollectionCount || 0;

    var form = document.querySelector('#desktop-filters-form');
    if (!form) { revealFacets(); return; }
    var container = form.closest('[data-section-id]');
    state.sectionId = container ? container.getAttribute('data-section-id') : null;
    if (!state.sectionId) { revealFacets(); return; }

    // Backstop: whatever happens below, the sidebar is never left hidden.
    // A slow or failed products.json then costs the visitor Warehouse's two
    // native facets rather than an empty card.
    var backstop = window.setTimeout(revealFacets, 4000);

    /* Shopify generates products.json cold on the first request and serves it
       from cache afterwards: measured on All Products (352 products, 378KB),
       1525ms cold against 274ms warm. Nothing client-side can speed up that
       first hit — but it does not have to be paid twice. The DERIVED products
       are cached, not the raw JSON: they are a fraction of the size and skip
       the re-parse as well as the re-fetch.

       sessionStorage, not localStorage, so the data cannot outlive the tab.
       The key carries the collection's product count, so adding or removing a
       product invalidates it. Stock can still drift within a session, which
       affects only the In stock facet — the grid itself is server-rendered. */
    var cacheKey = 'nr-facets:' + state.handle + ':' + (state.count || 0);
    var cached = null;
    try {
      var raw = window.sessionStorage.getItem(cacheKey);
      if (raw) cached = JSON.parse(raw);
    } catch (e) { cached = null; }

    if (cached && cached.length) {
      window.clearTimeout(backstop);
      state.products = cached;
      buildUI();
      readUrl();
      syncInputs();
      watchReload();
      apply();
      revealFacets();
      return;
    }

    fetchProducts().then(function (raw) {
      if (!raw.length) { revealFacets(); return; }
      state.products = raw.map(buildProduct);
      try { window.sessionStorage.setItem(cacheKey, JSON.stringify(state.products)); }
      catch (e) { /* quota or private mode — the fetch path still works */ }
      buildUI();
      readUrl();
      syncInputs();
      watchReload();
      apply();
    }).catch(function () {
      /* leave the native sidebar exactly as it was */
      revealFacets();
    }).then(function () {
      window.clearTimeout(backstop);
      revealFacets();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
